import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shield, Search } from 'lucide-react';
import { adminAuditApi } from '@/api/adminAudit';
import { securityEventsApi } from '@/api/securityEvents';
import { Badge, EmptyState, Loading, Modal } from '@/components/ui';
import type { AdminAuditLog, SecurityEvent, SecurityEventSeverity } from '@/types';
import { formatDateTime } from '@/utils/date';

type TabId = 'admin' | 'security';

const SEVERITY_OPTIONS: Array<{ value: SecurityEventSeverity | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'info', label: 'Info' },
];

const SINCE_OPTIONS = [
  { value: '24h', label: '24 hours', interval: '24 hours' },
  { value: '7d', label: '7 days', interval: '7 days' },
  { value: '30d', label: '30 days', interval: '30 days' },
] as const;

function sinceIso(key: (typeof SINCE_OPTIONS)[number]['value']): string {
  const match = SINCE_OPTIONS.find((opt) => opt.value === key)!;
  return new Date(Date.now() - parseSinceMs(match.interval)).toISOString();
}

function parseSinceMs(interval: string): number {
  const [amount, unit] = interval.split(' ');
  const n = Number(amount);
  if (unit.startsWith('hour')) return n * 60 * 60 * 1000;
  if (unit.startsWith('day')) return n * 24 * 60 * 60 * 1000;
  return 7 * 24 * 60 * 60 * 1000;
}

function formatMetadata(metadata: Record<string, unknown>): string {
  const entries = Object.entries(metadata);
  if (!entries.length) return '—';
  return entries
    .map(([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`)
    .join(' · ');
}

function formatTargetUserName(user: {
  email: string | null;
  first_name?: string | null;
  last_name?: string | null;
} | null | undefined): string {
  if (!user) return '—';
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return fullName || user.email || '—';
}

function severityBadgeVariant(severity: SecurityEventSeverity): 'danger' | 'warning' | 'info' | 'neutral' {
  if (severity === 'critical') return 'danger';
  if (severity === 'high') return 'warning';
  if (severity === 'medium') return 'info';
  return 'neutral';
}

function AdminActionsPanel() {
  const [draftOrgId, setDraftOrgId] = useState('');
  const [orgFilter, setOrgFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-audit-log', orgFilter],
    queryFn: () => adminAuditApi.list({ orgId: orgFilter || undefined }),
    staleTime: 15_000,
  });

  return (
    <>
      <div className="card p-4">
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            setOrgFilter(draftOrgId.trim());
          }}
        >
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-ink-secondary">Filter by org ID</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
              <input
                type="text"
                value={draftOrgId}
                onChange={(event) => setDraftOrgId(event.target.value)}
                placeholder="Paste organization UUID"
                className="h-10 w-full rounded-lg border border-navy-border bg-navy-card pl-9 pr-3 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40"
              />
            </div>
          </div>

          <button
            type="submit"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-indigo-600 px-4 text-sm font-medium text-white transition-colors hover:bg-indigo-500"
          >
            Apply filter
          </button>

          {orgFilter ? (
            <button
              type="button"
              onClick={() => {
                setDraftOrgId('');
                setOrgFilter('');
              }}
              className="inline-flex h-10 items-center justify-center rounded-lg border border-navy-border px-4 text-sm font-medium text-ink-secondary transition-colors hover:text-ink-primary"
            >
              Clear
            </button>
          ) : null}
        </form>
      </div>

      {isLoading ? (
        <Loading text="Loading audit log..." />
      ) : !data?.length ? (
        <EmptyState
          icon={<Shield className="h-8 w-8" />}
          title="No audit rows"
          description="No matching admin audit events were found."
        />
      ) : (
        <AdminAuditTable rows={data} />
      )}
    </>
  );
}

function AdminAuditTable({ rows }: { rows: AdminAuditLog[] }) {
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-navy-border bg-navy/30">
              <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">When</th>
              <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">Admin</th>
              <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">Action</th>
              <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">Org</th>
              <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">User</th>
              <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">Mode</th>
              <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">Metadata</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-navy-border">
            {rows.map((row) => (
              <tr key={row.id} className="align-top">
                <td className="px-4 py-3 text-xs text-ink-secondary">{formatDateTime(row.created_at)}</td>
                <td className="px-4 py-3 text-xs text-ink-primary">{row.admin_email}</td>
                <td className="px-4 py-3 text-xs text-ink-secondary">{row.action}</td>
                <td className="px-4 py-3 text-xs text-ink-secondary">
                  {row.target_org?.name ?? row.target_org_id ?? '—'}
                </td>
                <td className="px-4 py-3 text-xs text-ink-secondary">
                  {formatTargetUserName(row.target_user)}
                </td>
                <td className="px-4 py-3 text-xs text-ink-secondary capitalize">
                  {row.session_mode ?? '—'}
                </td>
                <td className="px-4 py-3 text-xs text-ink-muted">
                  <span className="block max-w-[420px] break-words">{formatMetadata(row.metadata)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SecurityEventsPanel() {
  const [draftOrgId, setDraftOrgId] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [severity, setSeverity] = useState<SecurityEventSeverity | 'all'>('all');
  const [eventType, setEventType] = useState('');
  const [sinceKey, setSinceKey] = useState<(typeof SINCE_OPTIONS)[number]['value']>('7d');
  const [selected, setSelected] = useState<SecurityEvent | null>(null);

  const since = useMemo(() => sinceIso(sinceKey), [sinceKey]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['security-events', orgFilter, severity, eventType, since],
    queryFn: () =>
      securityEventsApi.list({
        orgId: orgFilter || undefined,
        severity: severity === 'all' ? undefined : severity,
        eventType: eventType.trim() || undefined,
        since,
      }),
    staleTime: 15_000,
  });

  const eventTypes = useMemo(() => {
    const types = new Set((data ?? []).map((row) => row.event_type));
    return [...types].sort();
  }, [data]);

  return (
    <>
      <div className="card space-y-4 p-4">
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            setOrgFilter(draftOrgId.trim());
          }}
        >
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-ink-secondary">Filter by org ID</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
              <input
                type="text"
                value={draftOrgId}
                onChange={(event) => setDraftOrgId(event.target.value)}
                placeholder="Paste organization UUID"
                className="h-10 w-full rounded-lg border border-navy-border bg-navy-card pl-9 pr-3 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
              />
            </div>
          </div>
          <button
            type="submit"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-indigo-600 px-4 text-sm font-medium text-white transition-colors hover:bg-indigo-500"
          >
            Apply filter
          </button>
          {orgFilter ? (
            <button
              type="button"
              onClick={() => {
                setDraftOrgId('');
                setOrgFilter('');
              }}
              className="inline-flex h-10 items-center justify-center rounded-lg border border-navy-border px-4 text-sm font-medium text-ink-secondary transition-colors hover:text-ink-primary"
            >
              Clear
            </button>
          ) : null}
        </form>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {SEVERITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSeverity(opt.value)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  severity === opt.value
                    ? 'bg-indigo-600 text-white'
                    : 'border border-navy-border text-ink-secondary hover:text-ink-primary'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={eventType}
              onChange={(event) => setEventType(event.target.value)}
              className="h-9 rounded-lg border border-navy-border bg-navy-card px-3 text-xs text-ink-primary"
            >
              <option value="">All event types</option>
              {eventTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <select
              value={sinceKey}
              onChange={(event) => setSinceKey(event.target.value as (typeof SINCE_OPTIONS)[number]['value'])}
              className="h-9 rounded-lg border border-navy-border bg-navy-card px-3 text-xs text-ink-primary"
            >
              {SINCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error ? (
        <div className="card border border-danger/30 p-4 text-sm text-danger">
          {(error as Error).message.includes('forbidden')
            ? 'Access denied — super-admin required.'
            : (error as Error).message}
        </div>
      ) : isLoading ? (
        <Loading text="Loading security events..." />
      ) : !data?.length ? (
        <EmptyState
          icon={<Shield className="h-8 w-8" />}
          title="No security events"
          description="No matching security events were found in the selected window."
        />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy-border bg-navy/30">
                  <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">When</th>
                  <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">Severity</th>
                  <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">Event</th>
                  <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">Actor</th>
                  <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">Org</th>
                  <th className="px-4 py-3 text-start text-xs font-semibold text-ink-muted">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-border">
                {data.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer align-top transition-colors hover:bg-navy/20"
                    onClick={() => setSelected(row)}
                  >
                    <td className="px-4 py-3 text-xs text-ink-secondary">{formatDateTime(row.occurred_at)}</td>
                    <td className="px-4 py-3">
                      <Badge variant={severityBadgeVariant(row.severity)}>{row.severity}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-primary">{row.event_type}</td>
                    <td className="px-4 py-3 text-xs text-ink-secondary">{row.actor_email ?? '—'}</td>
                    <td className="px-4 py-3 text-xs text-ink-secondary">
                      {row.organization?.name ?? row.organization_id ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-xs capitalize text-ink-muted">{row.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.event_type ?? 'Security event'}
        size="lg"
      >
        {selected ? (
          <div className="space-y-4 text-sm">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs text-ink-muted">Occurred</p>
                <p className="text-ink-primary">{formatDateTime(selected.occurred_at)}</p>
              </div>
              <div>
                <p className="text-xs text-ink-muted">Severity</p>
                <Badge variant={severityBadgeVariant(selected.severity)}>{selected.severity}</Badge>
              </div>
              <div>
                <p className="text-xs text-ink-muted">Actor</p>
                <p className="text-ink-primary">{selected.actor_email ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs text-ink-muted">Source</p>
                <p className="capitalize text-ink-primary">{selected.source}</p>
              </div>
              <div>
                <p className="text-xs text-ink-muted">IP address</p>
                <p className="font-mono text-ink-primary">{selected.ip_address ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs text-ink-muted">User agent</p>
                <p className="break-all text-ink-primary">{selected.user_agent ?? '—'}</p>
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs text-ink-muted">Metadata</p>
              <pre className="max-h-64 overflow-auto rounded-lg border border-navy-border bg-navy/40 p-3 text-xs text-ink-secondary">
                {JSON.stringify(selected.metadata, null, 2)}
              </pre>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
}

export default function AdminAuditPage() {
  const [tab, setTab] = useState<TabId>('admin');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-primary">Security</h1>
          <p className="mt-1 text-sm text-ink-secondary">
            Admin action ledger and platform-wide security event feed for super-admin review.
          </p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-navy-border">
        <button
          type="button"
          onClick={() => setTab('admin')}
          className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'admin'
              ? 'border-indigo-500 text-ink-primary'
              : 'border-transparent text-ink-secondary hover:text-ink-primary'
          }`}
        >
          Admin actions
        </button>
        <button
          type="button"
          onClick={() => setTab('security')}
          className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'security'
              ? 'border-indigo-500 text-ink-primary'
              : 'border-transparent text-ink-secondary hover:text-ink-primary'
          }`}
        >
          Security events
        </button>
      </div>

      {tab === 'admin' ? <AdminActionsPanel /> : <SecurityEventsPanel />}
    </div>
  );
}
